import React, { useState } from 'react'; // ✅ Import useState
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { signupSchema } from '../forms/signupSchema';
import authService from '../services/authService';
import FormField from '../components/common/FormField';
import { showToast } from '../utils/showToast';
import { FcGoogle } from 'react-icons/fc';
import Modal from '../components/common/Modal'; // ✅ Import Modal component

const SignupForm: React.FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false); // ✅ Modal state
  const [modalMessage, setModalMessage] = useState(''); // ✅ Message state

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: signupSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        await authService.signup({
          name: values.name,
          email: values.email,
          password: values.password,
        });

        // ✅ On successful signup, set the modal state and message
        setModalMessage('Your student account has been created. It is now awaiting mentor approval. We will notify you once your account is ready.');
        setIsModalOpen(true);
        resetForm();
      } catch (err: any) {
        let errorMessage = '❌ Signup failed. Please try again.';
        if (err?.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err?.message) {
          errorMessage = err.message;
        }
        showToast(errorMessage, 'error');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleModalClose = () => {
    setIsModalOpen(false);
    navigate('/login'); // ✅ Redirect to login page on modal close
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit} className="space-y-5">
        <FormField
          label="Full Name"
          name="name"
          type="text"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && formik.errors.name ? formik.errors.name : ''}
          placeholder="John Doe"
        />

        <FormField
          label="Email"
          name="email"
          type="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && formik.errors.email ? formik.errors.email : ''}
          placeholder="you@example.com"
        />

        <FormField
          label="Password"
          name="password"
          type="password"
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && formik.errors.password ? formik.errors.password : ''}
          placeholder="••••••••"
        />

        <FormField
          label="Confirm Password"
          name="confirmPassword"
          type="password"
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.confirmPassword && formik.errors.confirmPassword ? formik.errors.confirmPassword : ''}
          placeholder="••••••••"
        />

        <button
          type="submit"
          disabled={formik.isSubmitting}
          className="w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:opacity-50"
        >
          {formik.isSubmitting ? 'Signing up...' : 'Sign Up'}
        </button>

        <div className="relative py-2 text-center text-sm text-gray-500">or</div>

        <button
          type="button"
          onClick={authService.googleAuth}
          className="w-full flex items-center justify-center gap-3 py-2 px-4 border border-gray-300 rounded hover:bg-gray-50 transition"
        >
          <FcGoogle className="text-xl" />
          <span className="text-sm font-medium text-gray-700">Sign up with Google</span>
        </button>
      </form>

      {/* ✅ Render the modal based on state */}
      <Modal isOpen={isModalOpen} onClose={handleModalClose} title="Signup Successful">
        <p className="text-gray-600">{modalMessage}</p>
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleModalClose}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </Modal>
    </>
  );
};

export default SignupForm;